/**
 * One Co-Living unit, as an expandable row (Co-Living redesign, Slice 3).
 *
 * A UNIT, NOT A PROPERTY. Two units at the same street address stay two rows.
 * Grouping them under one building is a product decision this page has
 * deliberately not taken; the Liegenschaft filter in the toolbar is how several
 * units of one property are looked at together.
 *
 * THE BADGES DESCRIBE THE WHOLE UNIT, ALWAYS. They are computed from every room
 * the unit has, never from the filtered subset shown below them. Filtering to
 * "Belegt" narrows the cards to the occupied rooms while the row still reads
 * "4 Zimmer · 2 belegt · 1 reserviert · 1 frei", because that is what the unit
 * is. The room map this replaces got that wrong: it counted whatever array it
 * was handed, so a filter silently rewrote the summary into a subtotal.
 *
 * THE MEDIA SLOT IS NOT A PHOTO AND DOES NOT PRETEND TO BE. There is no
 * building image anywhere in the loaded data and this slice adds no request for
 * one, so the slot is a themed icon tile, sized and placed like the thumbnail
 * it may one day become. Swapping in a real image is then a change of content,
 * not of layout.
 *
 * TWO SEPARATE ACTIONS, NEVER NESTED. Opening the unit's detail page is a link;
 * expanding the room list is a button. The row itself is neither, so there is
 * no control inside a control and no ambiguity about what a click does.
 *
 * WHAT THE UNIT COSTS IS READ, NEVER COMPUTED (FK2). The run-rate arrives on
 * the unit row of `GET /api/admin/units`, which this page already loads, as
 * `running_costs_monthly_chf` — the recurring monthly basis over `unit_costs`
 * plus the landlord deposit insurance premium, decided by
 * `unit_running_costs_service`. This row reads that one field and formats it.
 * It never sums room prices, never touches `landlordRentMonthly`,
 * `utilitiesMonthly` or `cleaningCostMonthly`, and never subtracts anything
 * from revenue: `current_revenue_chf` answers "this month" with lease
 * proration while the run-rate deliberately answers "a normal month", so a
 * margin built from the two would be arithmetic between different questions.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Building2, ChevronDown, ChevronRight } from "lucide-react";

import { Progress } from "../../../components/ui/progress";
import CoLivingRoomCard from "./CoLivingRoomCard";
import {
  formatUnitRunningCosts,
  splitUnitAddress,
  unitOccupancyPercent,
} from "./roomDisplayHelpers";
import { occupancyPercentText } from "../../../utils/occupancyPercent";

/**
 * Badge tones. Same vocabulary as the room cards and as
 * `apartments/UnitListRow.js`.
 */
const BADGE_TONES = {
  total: "bg-primary/10 text-primary dark:bg-primary/20",
  occupied: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  reserved: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  free: "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
};

function UnitBadge({ tone, value, label }) {
  return (
    <span
      className={`rounded-md px-2 py-[3px] text-[11px] font-semibold tabular-nums ${BADGE_TONES[tone]}`}
      data-unit-badge={label}
    >
      {value} {label}
    </span>
  );
}

export default function CoLivingUnitRow({
  unit,
  unitId,
  displayId,
  unitRooms,
  visibleRooms,
  statusByRoomId,
  // Room id -> `{ state, depositType }` of that room's CURRENT tenancy, or
  // `null`. Resolved once by `AdminRoomsPage`, exactly like `statusByRoomId`
  // beside it: this row decides nothing about a deposit, it hands each card
  // the entry already keyed to it.
  depositByRoomId,
  counts,
  isOpen,
  onToggle,
  detailPath,
}) {
  const { line1, line2 } = splitUnitAddress(unit);
  const panelId = `coliving-unit-rooms-${unitId}`;
  // From the SAME counts the badges are drawn from, so the figure and the four
  // numbers beside it can never disagree. Nothing is recomputed here and no
  // room status is decided here — `AdminRoomsPage` already did that once.
  const occupancy = unitOccupancyPercent(counts.occupied, counts.total);

  // ONE FIELD, STRAIGHT OFF THE UNIT ROW. `normalizeUnit` spreads the wire
  // object, so the snake_case contract key arrives untouched; there is no
  // camelCase alias to prefer and no legacy column to fall back to.
  const runningCosts = formatUnitRunningCosts(unit?.running_costs_monthly_chf);
  // WHY THE DASH'S WORDING IS CONDITIONAL. `unit_running_costs_service` and
  // `unit/UnitFinanceDetails` are careful about two different nulls, and this
  // row must not flatten them. `has_recurring_cost_basis: false` means the
  // contract WAS computed and found no classified recurring source — that, and
  // only that, earns the sentence the finance tab already uses verbatim. When
  // the aggregator raised, all three contract keys come back null and the
  // response still succeeds: that says the SUMMARY is unavailable and says
  // nothing about the unit's records, so the wording must not either.
  const runningCostsNote =
    unit?.running_costs_data_quality?.has_recurring_cost_basis === false
      ? "Keine klassifizierten laufenden Kosten erfasst"
      : "Laufende Kosten nicht bekannt";

  return (
    <div
      data-unit-row={displayId}
      className="overflow-hidden rounded-[12px] border border-border bg-card"
    >
      {/*
        WRAPS RATHER THAN CRUSHES. The row becomes one line at `sm`, but its
        content — identity, four badges, two actions — needs roughly 1100px of
        row width to sit comfortably. Between those two numbers the identity was
        the only shrinkable item with no floor, so it absorbed the entire
        deficit: at 1024px "CL-B01" measured 20px wide and rendered one letter
        per line. The identity now keeps a floor and the actions wrap beneath it
        instead (Slice 5, finding F2).
      */}
      <div className="flex flex-col gap-3 px-4 py-3.5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
        {/* Identity */}
        <div className="flex min-w-0 flex-1 items-center gap-3 sm:min-w-[12rem]">
          <span
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] bg-muted text-muted-foreground"
            aria-hidden="true"
          >
            <Building2 className="h-5 w-5" />
          </span>

          <div className="min-w-0">
            <h3 className="m-0 truncate font-mono text-[13px] font-semibold leading-tight text-foreground">
              {detailPath ? (
                <Link
                  to={detailPath}
                  className="rounded-[4px] hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {displayId}
                </Link>
              ) : (
                displayId
              )}
            </h3>
            {line1 ? (
              <p className="m-0 mt-0.5 truncate text-[12.5px] text-muted-foreground" title={line1}>
                {line1}
              </p>
            ) : null}
            {line2 ? (
              <p className="m-0 truncate text-[12px] text-muted-foreground">{line2}</p>
            ) : null}
          </div>
        </div>

        {/* Whole-unit summary — never a subtotal of the filtered view. */}
        <div className="flex flex-wrap items-center gap-1.5">
          <UnitBadge tone="total" value={counts.total} label="Zimmer" />
          <UnitBadge tone="occupied" value={counts.occupied} label="belegt" />
          <UnitBadge tone="reserved" value={counts.reserved} label="reserviert" />
          <UnitBadge tone="free" value={counts.free} label="frei" />
        </div>

        {/*
          AUSLASTUNG — how much of the unit is lived in today, as one number.
          It answers a question the four badges make the reader do arithmetic
          for, and it is derived from them rather than from a second source.

          IT STATES ONLY A SHARE. No threshold, no good/bad wording, no colour
          that changes with the value: 20 % is a fine number for a unit that
          has just been listed and a poor one for a unit that has been let for
          a year, and this row knows neither. The bar takes the theme's own
          primary tone at every value.

          THE WORD IS TAKEN ELSEWHERE, AND THE PRECISION NOW MATCHES. Three
          other admin surfaces label a per-unit figure "Auslastung":
          `unit/UnitHeroBar.js` (the backend `occupancy_rate`, falling back to
          this same occupied/total fold), `coLiving/CoLivingUnitsOverview.jsx`
          (occupied/total from the server's counts) and
          `AdminCoLivingDashboardPage` (the canonical
          `GET /api/admin/occupancy`). This row shipped as a whole percent with
          an em dash at zero rooms while the others printed one decimal and
          "0.0%", and reconciling them was called out here as a product
          decision rather than taken.

          IT HAS SINCE BEEN TAKEN (OC3): one decimal everywhere, and the em
          dash — this row's answer — is the rule at a zero denominator.
          `utils/occupancyPercent.js` holds it, so these surfaces cannot drift
          apart again without changing one file.

          THE BAR IS DECORATION, AND `aria-hidden` SAYS SO. The percentage is
          already written next to it in words, so announcing the bar as well
          would read the same figure out twice. `components/ui/progress.jsx`
          does forward `value` to the Radix root, so the bar could be
          announced correctly — this row keeps it hidden anyway, because the
          visible percentage already carries the information.

          A UNIT WITH NO ROOMS SHOWS AN EM DASH AND NO BAR. "0 %" would read as
          a measurement; there is none to report, and a bar at zero width is
          exactly what a fully vacant unit looks like.
        */}
        <div className="flex shrink-0 items-center gap-2" data-unit-occupancy>
          <span className="text-[11px] font-medium text-muted-foreground">
            Auslastung
          </span>
          <span
            className="text-[12px] font-semibold tabular-nums text-foreground"
            data-unit-occupancy-value
            title={occupancy === null ? "Keine Zimmer erfasst" : undefined}
          >
            {/* ONE DECIMAL NOW, and the dash comes from the same contract the
                other four surfaces use (OC3). `unitOccupancyPercent` still
                owns WHETHER there is an answer -- null for no rooms and for a
                count that is not a count -- and this only writes it down. */}
            {occupancy === null
              ? "—"
              : occupancyPercentText({ total: counts.total, rate: occupancy })}
            {occupancy === null ? (
              // THE DASH NEEDS WORDS, AND `title` IS NOT WORDS FOR EVERYONE.
              // A tooltip is unreachable by keyboard and by touch, and a bare
              // em dash is skipped outright at a screen reader's default
              // punctuation level — so without this the field would be
              // announced as "Auslastung" and then nothing. Same shape this
              // folder already settled on in `CoLivingOccupancyCell` and
              // `CoLivingOccupancyCalendar`: `title` as the pointer
              // convenience, `sr-only` as the actual carrier.
              <span className="sr-only">Keine Zimmer erfasst</span>
            ) : null}
          </span>
          {occupancy === null ? null : (
            <Progress
              value={occupancy}
              className="h-1.5 w-14"
              aria-hidden="true"
              data-unit-occupancy-bar
            />
          )}
        </div>

        {/*
          LAUFENDE KOSTEN — what this unit costs us in a normal month.

          SECONDARY, AND SHAPED LIKE IT. Same inline label-then-value pair as
          Auslastung beside it (11px muted label, 12px semibold tabular value),
          so it reads as one more piece of unit metadata rather than competing
          with the identity, the badges or the actions. No card, no colour of
          its own, nothing that changes with the value — a high run-rate is not
          a warning, it is a fact about a bigger flat.

          THE LABEL IS THE ONE THE EINHEITEN LIST ALREADY USES, verbatim, so a
          manager moving between /admin/rooms and /admin/units meets one name
          for one figure. `formatUnitRunningCosts` owns the money and the
          period; this component owns neither.

          ZERO IS PRINTED, NOT HIDDEN. "CHF 0 / Mt." is a measurement the
          backend stands behind; the em dash below is reserved for a null.

          NO BREAKDOWN AND NO DATA-QUALITY BADGE HERE, matching what the
          Einheiten list decided for the same figure: F2a also returns per
          bucket amounts and four data-quality counters, and a row that has to
          explain itself has stopped being scannable. The reasons live on the
          unit's own page, which is one click away through the identity link.
          What the flags DO reach is the dash's wording below — never the
          number, which stays exactly the backend's total.
        */}
        <div className="flex shrink-0 items-center gap-2" data-unit-running-costs>
          <span className="text-[11px] font-medium text-muted-foreground">
            Laufende Kosten
          </span>
          <span
            className="text-[12px] font-semibold tabular-nums text-foreground"
            data-unit-running-costs-value
            title={runningCosts === null ? runningCostsNote : undefined}
          >
            {runningCosts === null ? "—" : runningCosts}
            {runningCosts === null ? (
              // Same split this folder settled on for the Auslastung dash: the
              // `title` is the pointer convenience, the `sr-only` span is the
              // actual carrier. A bare em dash is skipped outright at a screen
              // reader's default punctuation level, so without this the field
              // would announce "Laufende Kosten" and then nothing.
              <span className="sr-only">{runningCostsNote}</span>
            ) : null}
          </span>
        </div>

        {/* The two actions travel together, so a wrap never strands one of
            them on a line of its own. */}
        <div className="flex shrink-0 items-center gap-2 self-start sm:ml-auto sm:self-center">
          {detailPath ? (
            <Link
              to={detailPath}
              aria-label={`Einheit öffnen: ${displayId}`}
              className="inline-flex shrink-0 items-center gap-1 rounded-[8px] px-2 py-1.5 text-[12px] text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Einheit öffnen
              <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          ) : null}

          <button
            type="button"
            onClick={onToggle}
            aria-expanded={isOpen}
            aria-controls={panelId}
            aria-label={`${displayId} Zimmer ${isOpen ? "ausblenden" : "anzeigen"}`}
            className="inline-flex shrink-0 items-center gap-1 rounded-[8px] border border-border px-2.5 py-1.5 text-[12px] text-muted-foreground transition-colors hover:border-foreground/20 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {isOpen ? "Zimmer ausblenden" : "Zimmer anzeigen"}
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      {isOpen ? (
        <div id={panelId} className="border-t border-border bg-muted/30 px-4 py-3.5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {visibleRooms.map((room, i) => (
              <CoLivingRoomCard
                key={room.roomId || room.id || i}
                room={room}
                index={i}
                status={statusByRoomId.get(String(room.roomId ?? room.id))}
                deposit={depositByRoomId?.get(String(room.roomId ?? room.id))}
                wgSize={unitRooms.length}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
